import { useState, useEffect } from 'react';

// Material UI
import { styled } from '@mui/material/styles';
import EventIcon from '@mui/icons-material/Event';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import {
    Box,
    Typography,
    CircularProgress,
    useMediaQuery,
    Menu,
    MenuItem,
    ListItemIcon,
    Button,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// Components
import { ControlBar, ControlBarHeader, Page } from 'components/ui/layout';
import { EventListHeader, EventListRow } from './EventListUI';
import FullScreenModal from 'components/popups/FullScreenModal';
import EventCreator from 'components/forms/EventCreator';
import EventDuplicator from 'components/forms/EventDuplicator';

// Utility
import { useAuth } from 'context/auth';
import ReactGA from 'react-ga';
import { useNotification } from 'context/notification';
import { getEvents } from './EventListAPI';

export default function EventList(props) {
    // State
    const [state, setState] = useState({
        modalOpen: false, // Opens the modal. Used for creating, editing, and copying events
        events: [],
        status: 'loading', // loading, error, success
        copyId: null, // ID of event being copied
        parentMode: 0,
        parentEventId: null,
        refreshCount: 0, // Increment to refresh events
    });

    const [createMenuAnchor, setCreateMenuAnchor] = useState(null);
    const [liveExpanded, setLiveExpanded] = useState(true);
    const [upcomingExpanded, setUpcomingExpanded] = useState(true);
    const [concludedExpanded, setConcludedExpanded] = useState(true);

    // Hooks
    const { auth } = useAuth();
    const { notify } = useNotification();

    const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('md'));

    const hostId = auth.hostId;

    // Load/Reload list of events
    useEffect(() => {
        getEvents(hostId)
            .then((events) =>
                setState((state) => ({ ...state, events: events, status: 'success' })),
            )
            .catch(() => {
                notify.error('Unable to find events. Try refreshing the page.');
                setState((state) => ({ ...state, status: 'error' }));
            });
        // eslint-disable-next-line
    }, [hostId, state.refreshCount]);

    // State Functions
    const handleModalOpen = (parentMode = 0, parentEventId = null) => {
        setCreateMenuAnchor(null);
        setState((state) => ({
            ...state,
            modalOpen: true,
            copyId: null,
            parentMode,
            parentEventId,
        }));
    };
    const createButton = (
        <>
            <Button
                variant="contained"
                endIcon={<ArrowDropDownIcon />}
                onClick={(event) => setCreateMenuAnchor(event.currentTarget)}
            >
                New Event Type
            </Button>
            <Menu
                anchorEl={createMenuAnchor}
                open={Boolean(createMenuAnchor)}
                onClose={() => setCreateMenuAnchor(null)}
            >
                <MenuItem onClick={() => handleModalOpen(0)}>
                    <ListItemIcon>
                        <EventIcon fontSize="small" color='info' />
                    </ListItemIcon>
                    Event
                </MenuItem>
                <MenuItem onClick={() => handleModalOpen(1)}>
                    <ListItemIcon>
                        <EventRepeatIcon fontSize="small" color='info' />
                    </ListItemIcon>
                    Season
                </MenuItem>
            </Menu>
        </>
    );

    const handleModalClose = () => {
        setState({ ...state, modalOpen: false, copyId: null });
    };

    const handleEventSelect = (id) => {
        props.history.push(`/events/${id}/`);
        ReactGA.event({ category: 'Events', action: 'View Event', label: `${hostId}` });
    };

    const handleEventCopy = (id) => {
        setState((state) => ({ ...state, modalOpen: true, copyId: id }));
    };

    const handleCreate = (eventId) => {
        props.history.push(`/events/${eventId}/`);
        ReactGA.event({ category: 'Events', action: 'Create Event', label: `${hostId}` });
    };

    const handleCopy = () => {
        setState((state) => ({
            ...state,
            modalOpen: false,
            copyId: null,
            refreshCount: state.refreshCount + 1,
        }));
        ReactGA.event({ category: 'Events', action: 'Copy Event', label: `${hostId}` });
    };

    let modalHeader = state.parentMode ? 'Create Season' : 'Create Event';
    if (state.copyId) {
        const source = state.events.find((event) => event.event_id === state.copyId);
        modalHeader = Number(source?.parent_mode) === 1 ? 'Copy Season' : 'Copy Event';
    }

    if (state.status === 'loading') {
        return (
            <Page>
                <Status>
                    <CircularProgress disableShrink />
                </Status>
            </Page>
        );
    }

    if (state.status === 'error' || Array.isArray(state.events) === false) {
        return (
            <Page>
                <Status>
                    <Typography>There was a problem loading events</Typography>
                </Status>
            </Page>
        );
    }

    if (state.events.length === 0) {
        // No events have been created for this host. Return early.
        // Make sure to also render the EventCreator modal here so hosts can still create their first event
        return (
            <Page>
                <ControlBar>
                    <ControlBarHeader>Upcoming</ControlBarHeader>
                    {createButton}
                </ControlBar>
                <Status>
                    <Typography variant="h6">No upcoming events found</Typography>
                </Status>

                <FullScreenModal
                    open={state.modalOpen}
                    onClose={handleModalClose}
                    title={modalHeader}
                    cancelText="Cancel"
                >
                    {!state.copyId && (
                        <EventCreator
                            parentMode={state.parentMode}
                            parentEventId={state.parentEventId}
                            hostId={hostId}
                            onCreate={handleCreate}
                            onCancel={handleModalClose}
                        />
                    )}
                </FullScreenModal>
            </Page>
        );
    }

    /**
     * Split events into:
     *  Live: end_date ascending (Is this the right way to order live events?)
     *  Upcoming: end_date ascending
     *  Concluded: end_date descending
     *
     * Upcoming events show up first
     * Both upcoming/concluded events show most recent events closer to the top
     */
    const concludedEvents = state.events.filter((e) => e.status === 'concluded');
    const liveEvents = state.events.filter((e) => e.status === 'live');
    const upcomingEvents = state.events.filter((e) => e.status === 'upcoming').reverse();

    // Render the 'New Event' button on the section that appears first on the screen
    // Note: We check each sections length in the reverse order that they appear on screen so that the 'New Event' button shows up on the first section available
    let renderButtonOn;
    if (concludedEvents.length) renderButtonOn = 'concluded';
    if (upcomingEvents.length) renderButtonOn = 'upcoming';
    if (liveEvents.length) renderButtonOn = 'live';

    // Table view to display events
    return (
        <Page>
            {liveEvents.length > 0 && (
                <Box mb={8}>
                    <ControlBar>
                        <ControlBarHeader expanded={liveExpanded} toggleExpanded={setLiveExpanded}>
                            Live
                        </ControlBarHeader>
                        {renderButtonOn === 'live' && createButton}
                    </ControlBar>

                    {liveExpanded === true && (
                        <>
                            <EventListHeader isDesktop={isDesktop} />
                            {liveEvents.map((event) => (
                                <EventListRow
                                    key={event.event_id}
                                    isDesktop={isDesktop}
                                    event={event}
                                    onEventSelect={handleEventSelect}
                                    onCopy={handleEventCopy}
                                    onAddEvent={(seasonId) => handleModalOpen(0, seasonId)}
                                />
                            ))}
                        </>
                    )}
                    {liveExpanded === false && (
                        <Typography textAlign={'center'}>
                            {liveEvents.length} live {liveEvents.length === 1 ? 'event' : 'events'}{' '}
                            hidden
                        </Typography>
                    )}
                </Box>
            )}

            {upcomingEvents.length > 0 && (
                <Box mb={8}>
                    <ControlBar>
                        <ControlBarHeader
                            expanded={upcomingExpanded}
                            toggleExpanded={setUpcomingExpanded}
                        >
                            Upcoming
                        </ControlBarHeader>
                        {renderButtonOn === 'upcoming' && createButton}
                    </ControlBar>
                    {upcomingExpanded === true && (
                        <>
                            <EventListHeader isDesktop={isDesktop} />
                            {upcomingEvents.map((event) => (
                                <EventListRow
                                    key={event.event_id}
                                    isDesktop={isDesktop}
                                    event={event}
                                    onEventSelect={handleEventSelect}
                                    onCopy={handleEventCopy}
                                    onAddEvent={(seasonId) => handleModalOpen(0, seasonId)}
                                />
                            ))}
                        </>
                    )}
                    {upcomingExpanded === false && (
                        <Typography textAlign={'center'}>
                            {upcomingEvents.length} upcoming{' '}
                            {upcomingEvents.length === 1 ? 'event' : 'events'} hidden
                        </Typography>
                    )}
                </Box>
            )}

            {concludedEvents.length > 0 && (
                <Box mb={8}>
                    <ControlBar>
                        <ControlBarHeader
                            expanded={concludedExpanded}
                            toggleExpanded={setConcludedExpanded}
                        >
                            Concluded
                        </ControlBarHeader>
                        {renderButtonOn === 'concluded' && createButton}
                    </ControlBar>
                    {concludedExpanded === true && (
                        <>
                            <EventListHeader isDesktop={isDesktop} />
                            {concludedEvents.map((event) => (
                                <EventListRow
                                    key={event.event_id}
                                    isDesktop={isDesktop}
                                    event={event}
                                    onEventSelect={handleEventSelect}
                                    onCopy={handleEventCopy}
                                    onAddEvent={(seasonId) => handleModalOpen(0, seasonId)}
                                />
                            ))}
                        </>
                    )}
                    {concludedExpanded === false && (
                        <Typography textAlign={'center'}>
                            {concludedEvents.length} concluded{' '}
                            {concludedEvents.length === 1 ? 'event' : 'events'} hidden
                        </Typography>
                    )}
                </Box>
            )}

            <FullScreenModal
                open={state.modalOpen}
                onClose={handleModalClose}
                title={modalHeader}
                cancelText="Cancel"
            >
                {state.copyId && (
                    <EventDuplicator
                        eventId={state.copyId}
                        hostId={hostId}
                        onCopy={handleCopy}
                        onCancel={handleModalClose}
                    />
                )}
                {!state.copyId && (
                    <EventCreator
                        parentMode={state.parentMode}
                        parentEventId={state.parentEventId}
                        hostId={hostId}
                        onCreate={handleCreate}
                        onCancel={handleModalClose}
                    />
                )}
            </FullScreenModal>
        </Page>
    );
}

const Status = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: 156,
}));
